// ./src/components/features/UserInvitations/UserInvitationsList/UserInvitationsList.tsx
//
// List component for the UserInvitations page.
// Renders invitations as custom cards with swipeable actions on mobile
// and hover actions on desktop — matching the UsersList pattern.
//
// Layout design (desktop, sm+):
//   ┌──────────────────────────────────────────────────────────────┐
//   │  [AV]  First Last     ADMIN   ● Active / ⚠ Expired  [↻][✏][🗑]│
//   │        ┌──────────────────┬──────────────────────────┐      │
//   │        │ ✉ EMAIL          │ 📅 EXPIRES               │      │
//   │        │ user@example.com │ Mar 15, 2026             │      │
//   │        └──────────────────┴──────────────────────────┘      │
//   └──────────────────────────────────────────────────────────────┘
//
// Layout design (mobile, <sm):
//   ┌──────────────────────────┐
//   │  [AV]  First Last       │  ← swipeable for actions
//   │  ADMIN   ● Active       │
//   │  ✉ EMAIL                │
//   │  user@example.com       │
//   │  📅 EXPIRES              │
//   │  Mar 15, 2026           │
//   └──────────────────────────┘
//
// Context support:
// - Reads from UserInvitationsProvider when available; props override context.

import React, { useMemo, useCallback } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Box,
  ActionIcon,
  Tooltip,
  SimpleGrid,
  Skeleton,
  Table,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { formatDateTime } from "@em/utils";

import { UserInvitation } from "@/utils/api/UserInvitationService";

import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import { useOptionalUserInvitationsContext } from "@/contexts/UserInvitationsContext";
import { useAppEnvironment } from "@/contexts";

import { AppIcon } from "@/components/generic/AppIcon";
import { EmptyState } from "@/components/generic/EmptyState";
import { ErrorAlert } from "@/components/generic/ErrorAlert";
import { LoadMoreButton } from "@/components/generic/LoadMoreButton";
import { SwipeableCard } from "@/components/generic/SwipeableCard";
import { SwipeAction, useSwipeableList } from "@/components/generic/SwipeableCard";
import { CardSkeletonActions, CardSkeletonLines, countCardActions } from "@/components/generic/CardListSkeleton/CardSkeletonParts";

// ============================================
// Types
// ============================================

export interface UserInvitationsListProps {
  /** Array of invitations to display. Optional — reads from context. */
  userInvitations?: UserInvitation[];

  /** Loading state for initial load. Optional — reads from context. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEdit?: (invitation: UserInvitation) => void;

  /** Callback when delete action is clicked */
  onDelete?: (invitation: UserInvitation) => void;

  /** Callback when resend action is clicked */
  onResend?: (invitation: UserInvitation) => void;

  /** ID of invitation currently being removed. Optional — reads from context. */
  removingId?: string | null;

  /** ID of invitation currently being resent. Optional — reads from context. */
  resendingId?: string | null;

  /** ID of recently edited/added invitation to highlight */
  highlightedId?: string | null;

  /** Callback when add/invite button is clicked (for empty state) */
  onAdd?: () => void;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// CSS for hover actions (injected once)
// ============================================

const ACTIONS_CLASS = "invitation-card__actions";
const CARD_CLASS = "invitation-card";

const CARD_STYLES = `
  .${ACTIONS_CLASS} {
    opacity: 0;
    pointer-events: none;
    transition: opacity 150ms ease;
  }
  @media (hover: hover) {
    .${CARD_CLASS}:hover .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
  .${CARD_CLASS}:focus-within .${ACTIONS_CLASS} {
    opacity: 1;
    pointer-events: auto;
  }
  @media (hover: none) {
    .${ACTIONS_CLASS} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-invitation-card", "");
  style.textContent = CARD_STYLES;
  document.head.appendChild(style);
  stylesInjected = true;
};

// ============================================
// Loading Skeleton
// ============================================

// ⓘ `actions` = the tablet card's hover buttons (resend / edit / remove), drawn tablet-only: a phone swipes
// (▶ check-card-skeletons).
// ⓘ The name row is ONE line on a phone too: the role / status badges wrap under the name only past ~13 characters
// (measured, ROADMAP #127), and most names are 10–11.
const LoadingSkeleton: React.FC<{ actions: number }> = ({ actions }) => {
  return (
    <Stack gap="md">
      {Array.from({ length: 4 }).map((_, index) => (
        <Paper key={index} p="md" withBorder>
          <Group gap="md" wrap="nowrap" align="flex-start">
            <Skeleton height={44} width={44} circle style={{ flexShrink: 0 }} />
            <Stack gap="sm" style={{ flex: 1 }}>
              <Group justify="space-between" wrap="nowrap" gap="sm">
                <CardSkeletonLines count={1} sizes={['lg']} />
                <CardSkeletonActions count={actions} />
              </Group>
              {/* Not text rows: the info fields (Email, Expires) are a grid — stacked on a phone, side by side on a
                  tablet, the real card's own breakpoints — and each is two lines: its label over its value. */}
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
                <Skeleton height={41} width="80%" />
                <Skeleton height={41} width="60%" />
              </SimpleGrid>
            </Stack>
          </Group>
        </Paper>
      ))}
    </Stack>
  );
};

// ============================================
// Avatar Circle
// ============================================

interface AvatarCircleProps {
  name: string;
  color?: string;
}

const AvatarCircle: React.FC<AvatarCircleProps> = ({ name, color = "blue" }) => {
  const initials = useMemo(() => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (name[0] || "?").toUpperCase();
  }, [name]);

  return (
    <Box
      style={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: `var(--mantine-color-${color}-0)`,
        border: `2px solid var(--mantine-color-${color}-2)`,
        flexShrink: 0,
      }}
    >
      <Text size="md" fw={600} c={color}>
        {initials}
      </Text>
    </Box>
  );
};

// ============================================
// Info Field — icon + label + value
// ============================================

interface InfoFieldProps {
  icon: string;
  label: string;
  value: string;
  valueColor?: string;
  valueFw?: number;
  suffix?: React.ReactNode;
}

const InfoField: React.FC<InfoFieldProps> = ({
  icon,
  label,
  value,
  valueColor,
  valueFw,
  suffix,
}) => (
  <Group gap={8} wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
    <Group gap={4} wrap="nowrap" align="center" style={{ flexShrink: 0, height: 18 }}>
      <AppIcon iconName={icon} size={14} />
    </Group>
    <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500} lh={1.3}>
        {label}
      </Text>
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="md" c={valueColor} fw={valueFw} truncate style={{ minWidth: 0 }}>
          {value}
        </Text>
        {suffix}
      </Group>
    </Stack>
  </Group>
);

// ============================================
// Expiration helpers
// ============================================

const isExpired = (expiresAt?: string | null): boolean => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

// ============================================
// Single Invitation Card
// ============================================

interface InvitationCardProps {
  invitation: UserInvitation;
  onClick?: (invitation: UserInvitation) => void;
  onEdit?: (invitation: UserInvitation) => void;
  onDelete?: (invitation: UserInvitation) => void;
  onResend?: (invitation: UserInvitation) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isResending: boolean;
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const InvitationCardComponent: React.FC<InvitationCardProps> = ({
  invitation,
  onClick,
  onEdit,
  onDelete,
  onResend,
  highlighted,
  isDeleting,
  isResending,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["userInvitations", "common"]);

  injectStyles();

  const fullName = [invitation.firstName, invitation.lastName]
    .filter(Boolean)
    .join(" ") || invitation.email || "—";

  const expired = isExpired(invitation.expiresAt);
  const hasActions = !!onEdit || !!onDelete || !!onResend;

  // ── Handlers ──

  const handleClick = useCallback(() => {
    onClick?.(invitation);
  }, [onClick, invitation]);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onEdit?.(invitation);
    },
    [onEdit, invitation]
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onDelete?.(invitation);
    },
    [onDelete, invitation]
  );

  const handleResendClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onResend?.(invitation);
    },
    [onResend, invitation]
  );

  // ── Swipe actions (mobile) ──

  const leftSwipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    if (onResend) {
      actions.push({
        key: "resend",
        secondary: true,
        icon: "Send",
        label: t("userInvitations:actions.resend", { defaultValue: "Resend" }),
        color: "teal",
        onClick: () => onResend(invitation),
        loading: isResending,
        disabled: isDeleting || isResending,
      });
    }
    if (onEdit) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(invitation),
        disabled: isDeleting || isResending,
      });
    }
    if (onDelete) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(invitation),
        loading: isDeleting,
        disabled: isDeleting || isResending,
      });
    }
    return actions;
  }, [onResend, onEdit, onDelete, invitation, isDeleting, isResending, t]);

  // ── Hover actions (desktop) ──

  const hoverActionsEl = hasActions && (
    <Group gap={4} wrap="nowrap" className={ACTIONS_CLASS}>
      {onResend && (
        <Tooltip
          label={t("userInvitations:actions.resend", { defaultValue: "Resend invitation" })}
          withArrow
          position="bottom"
        >
          <ActionIcon
            variant="subtle"
            color="teal"
            size="md"
            onClick={handleResendClick}
            loading={isResending}
            disabled={isDeleting || isResending}
            aria-label={t("userInvitations:actions.resend", { defaultValue: "Resend" })}
          >
            <AppIcon iconName="Send" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={handleEditClick}
            disabled={isDeleting || isResending}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={handleDeleteClick}
            loading={isDeleting}
            disabled={isDeleting || isResending}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  // ── Status badge ──

  const statusBadge = expired ? (
    <Badge size="sm" color="red" variant="light">
      {t("userInvitations:status.expired", { defaultValue: "Expired" })}
    </Badge>
  ) : (
    <Badge size="sm" color="green" variant="light">
      {t("userInvitations:status.active", { defaultValue: "Active" })}
    </Badge>
  );

  // ── Expiration display ──

  const expiresFormatted = invitation.expiresAt
    ? formatDateTime(invitation.expiresAt, "date")
    : t("userInvitations:list.noExpiration", { defaultValue: "No expiration" });

  // ── Card content ──

  const cardContent = (
    <Paper
      p="md"
      withBorder
      bg={highlighted ? "green.0" : undefined}
      opacity={isDeleting ? 0.5 : 1}
      className={CARD_CLASS}
      style={{
        cursor: onClick ? "pointer" : undefined,
        transition: "box-shadow 150ms ease, opacity 150ms ease",
      }}
      onClick={handleClick}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {/* Avatar */}
        <AvatarCircle name={fullName} color="blue" />

        {/* Content */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: Name + Role + Status + Actions */}
          <Group justify="space-between" wrap="nowrap" gap="sm">
            <Group gap="sm" wrap="wrap" style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="lg" truncate style={{ minWidth: 0 }}>
                {fullName}
              </Text>
              {invitation.userRole?.roleName && (
                <Badge variant="light" size="sm" color="violet">
                  {invitation.userRole.roleName}
                </Badge>
              )}
              {statusBadge}
            </Group>
            {/* Desktop hover actions */}
            <Box visibleFrom="sm" style={{ flexShrink: 0 }}>
              {hoverActionsEl}
            </Box>
          </Group>

          {/* Row 2: Info fields in two-column grid on desktop */}
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs" verticalSpacing="xs">
            {invitation.email && (
              <InfoField
                icon="Mail"
                label={t("userInvitations:list.email", { defaultValue: "Email" })}
                value={invitation.email}
              />
            )}
            <InfoField
              icon="Calendar"
              label={t("userInvitations:list.expires", { defaultValue: "Expires" })}
              value={expiresFormatted}
              valueColor={expired ? "red" : undefined}
              valueFw={expired ? 500 : undefined}
            />
          </SimpleGrid>
        </Stack>
      </Group>
    </Paper>
  );

  // ── Mobile: swipeable / Desktop: plain ──

  if (leftSwipeActions.length > 0) {
    return (
      <>
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={leftSwipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-default)"
          >
            {cardContent}
          </SwipeableCard>
        </Box>
        <Box visibleFrom="sm">
          {cardContent}
        </Box>
      </>
    );
  }

  return cardContent;
};

const InvitationCard = React.memo(InvitationCardComponent);

// ============================================
// Desktop table row
// ============================================
//
// ⚠ A list page shows a TABLE on large screens and cards below — cards are a phone affordance.
// This list and `UsersList` were the only two in the product still showing cards at every width
// (owner, 2026-09-28).

interface InvitationRowProps {
  invitation: UserInvitation;
  onEdit?: (invitation: UserInvitation) => void;
  onDelete?: (invitation: UserInvitation) => void;
  onResend?: (invitation: UserInvitation) => void;
  highlighted: boolean;
  isDeleting: boolean;
  isResending: boolean;
}

const InvitationTableRow: React.FC<InvitationRowProps> = ({
  invitation,
  onEdit,
  onDelete,
  onResend,
  highlighted,
  isDeleting,
  isResending,
}) => {
  const { t } = useTranslation(["userInvitations", "common"]);

  const fullName =
    [invitation.firstName, invitation.lastName].filter(Boolean).join(" ") || invitation.email || "\u2014";
  const expired = isExpired(invitation.expiresAt);

  const expiresFormatted = invitation.expiresAt
    ? formatDateTime(invitation.expiresAt, "date")
    : t("userInvitations:list.noExpiration", { defaultValue: "No expiration" });

  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? "var(--mantine-color-green-0)" : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      <Table.Td style={{ width: 52 }}>
        <AvatarCircle name={fullName} color="blue" />
      </Table.Td>

      <Table.Td>
        <Text size="md" fw={600} lineClamp={1}>
          {fullName}
        </Text>
      </Table.Td>

      <Table.Td>
        <Text size="md" lineClamp={1}>
          {invitation.email || "\u2014"}
        </Text>
      </Table.Td>

      <Table.Td style={{ width: 160 }}>
        {invitation.userRole?.roleName && (
          <Badge variant="light" size="sm" color="violet">
            {invitation.userRole.roleName}
          </Badge>
        )}
      </Table.Td>

      <Table.Td style={{ width: 150 }}>
        {/* ⚠ An expired invitation is the one thing a host has to act on, so the date carries the
            same red the badge does rather than sitting in neutral grey beside it. */}
        <Text size="md" c={expired ? "red" : undefined} fw={expired ? 500 : undefined}>
          {expiresFormatted}
        </Text>
      </Table.Td>

      <Table.Td style={{ width: 110 }}>
        {expired ? (
          <Badge size="sm" color="red" variant="light">
            {t("userInvitations:status.expired", { defaultValue: "Expired" })}
          </Badge>
        ) : (
          <Badge size="sm" color="green" variant="light">
            {t("userInvitations:status.active", { defaultValue: "Active" })}
          </Badge>
        )}
      </Table.Td>

      <Table.Td style={{ width: 130 }}>
        <Group gap={4} wrap="nowrap">
          {onResend && (
            <Tooltip label={t("userInvitations:actions.resend", { defaultValue: "Resend invitation" })} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="teal"
                size="md"
                onClick={() => onResend(invitation)}
                loading={isResending}
                disabled={isDeleting || isResending}
                aria-label={t("userInvitations:actions.resend", { defaultValue: "Resend" })}
              >
                <AppIcon iconName="Send" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="blue"
                size="md"
                onClick={() => onEdit(invitation)}
                disabled={isDeleting || isResending}
                aria-label={t("common:buttons.edit")}
              >
                <AppIcon iconName="Edit" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
              <ActionIcon
                variant="subtle"
                color="red"
                size="md"
                onClick={() => onDelete(invitation)}
                loading={isDeleting}
                disabled={isDeleting || isResending}
                aria-label={t("common:buttons.delete")}
              >
                <AppIcon iconName="Trash" size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
};

/** ⚠ Column widths mirror the real row, so the table does not jump when the data lands. */
const InvitationTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: 52 }}>
      <Skeleton height={36} width={36} circle />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width={150} />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="70%" maw={220} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={20} width={80} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 150 }}>
      <Skeleton height={16} width={90} />
    </Table.Td>
    <Table.Td style={{ width: 110 }}>
      <Skeleton height={20} width={62} radius="xl" />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={20} width={84} />
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Main Component
// ============================================

const UserInvitationsListComponent: React.FC<UserInvitationsListProps> = ({
  userInvitations: invitationsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEdit,
  onDelete,
  onResend,
  removingId: removingIdProp,
  resendingId: resendingIdProp,
  highlightedId,
  onAdd,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["userInvitations", "common"]);

  // ── Context vs Props resolution ──
  const invitationsContext = useOptionalUserInvitationsContext();

  const invitations = invitationsProp ?? invitationsContext?.userInvitations ?? [];
  const loading = loadingProp ?? invitationsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? invitationsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? invitationsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? invitationsContext?.loadMore;
  const removingId = removingIdProp ?? invitationsContext?.removingId ?? null;
  const resendingId = resendingIdProp ?? invitationsContext?.resendingId ?? null;
  const { isDesktop } = useAppEnvironment();

  /**
   * ⚠⚠ "NOT LOADED YET" IS NOT "EMPTY". Deciding on `!loading && invitations.length === 0` is TRUE
   * on the first render, before the read has started — so the page painted "No invitations found",
   * then a skeleton, then the table. Until the first read has COME BACK, the honest answer is a
   * skeleton. ⚠ A caller passing `userInvitations` as a prop has already loaded them — without that
   * fallback a props-only caller would sit on a skeleton for ever. See `UsersList` for the full note.
   */
  const isLoaded = invitationsContext?.isLoaded ?? invitationsProp !== undefined;

  /**
   * ⚠⚠ A FAILED READ IS NOT AN EMPTY LIST — AND THIS ONE HAS BEEN TELLING THAT LIE.
   *
   * `list()` sets `isLoaded` true and the array to [] on BOTH the error and the exception path, so a
   * read that failed rendered "No invitations found — send invitations to add team members": the
   * host is told their data does not exist, nothing they do fixes it, and nothing says why. The rule
   * is error → no-match → first-run, in that order, and neither this list nor `UsersList` had the
   * first branch at all.
   * ⚠ No `onDismiss`: dismissing would restore the lie.
   */
  const lastError = invitationsContext?.lastError ?? null;
  const readFailed = isLoaded && invitations.length === 0 && !!lastError;

  // Manage swipeable cards
  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  // Entrance animation
  const { hasAnimated } = useEntranceAnimation({ itemCount: invitations.length });

  // ── Handlers ──

  const handleClick = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onEdit?.(invitation);
    },
    [onEdit, closeAllCards]
  );

  const handleEdit = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onEdit?.(invitation);
    },
    [onEdit, closeAllCards]
  );

  const handleDelete = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onDelete?.(invitation);
    },
    [onDelete, closeAllCards]
  );

  const handleResend = useCallback(
    (invitation: UserInvitation) => {
      closeAllCards();
      onResend?.(invitation);
    },
    [onResend, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (invitationId: string, isOpen: boolean) => {
      if (isOpen) openCard(invitationId);
    },
    [openCard]
  );

  // ── Nothing read back yet ── (see `isLoaded` above — NOT the same as "empty")
  //
  // ⚠ ONLY the first read. A REFETCH keeps what is on screen — the rows, or the empty state — and
  // ContentLoadingOverlay says it is reloading. Re-showing the skeleton whenever the list happens
  // to be empty is what made an empty page read skeleton → "none" → skeleton → "none".

  const showSkeleton = !isLoaded;

  // ── A read that FAILED ── never falls through to "nothing here"

  if (!showSkeleton && readFailed) {
    return (
      <ErrorAlert
        error={lastError}
        title={t("userInvitations:errors.loadTitle", { defaultValue: "Couldn't load invitations" })}
        page={t("userInvitations:pageName", { defaultValue: "Invitations" })}
        operation="userInvitationList"
      />
    );
  }

  // ── Empty state ── only once we actually know the set is empty

  if (!showSkeleton && invitations.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("userInvitations:emptyState.noMatch", { defaultValue: "No invitations match your search." })}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl">
        <EmptyState
          title={t("userInvitations:emptyState.title", { defaultValue: "No invitations found" })}
          description={t("userInvitations:emptyState.description", {
            defaultValue: "Send invitations to add team members.",
          })}
          iconName="Mail"
          actionLabel={t("userInvitations:emptyState.actionLabel", { defaultValue: "Invite User" })}
          onAction={onAdd}
          size="md"
        />
      </Paper>
    );
  }

  // ── Desktop: a table, like every other list page ──

  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 52 }} />
                <Table.Th>{t("userInvitations:list.name", { defaultValue: "Name" })}</Table.Th>
                <Table.Th>{t("userInvitations:list.email", { defaultValue: "Email" })}</Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t("userInvitations:list.role", { defaultValue: "Role" })}
                </Table.Th>
                <Table.Th style={{ width: 150 }}>
                  {t("userInvitations:list.expires", { defaultValue: "Expires" })}
                </Table.Th>
                <Table.Th style={{ width: 110 }}>
                  {t("userInvitations:list.status", { defaultValue: "Status" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => <InvitationTableRowSkeleton key={i} />)
                : invitations.map((invitation) => (
                    <InvitationTableRow
                      key={invitation.id}
                      invitation={invitation}
                      onEdit={onEdit ? handleEdit : undefined}
                      onDelete={onDelete ? handleDelete : undefined}
                      onResend={onResend ? handleResend : undefined}
                      highlighted={invitation.id === highlightedId}
                      isDeleting={removingId === invitation.id}
                      isResending={resendingId === invitation.id}
                    />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>

        {!showSkeleton && (
          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadingMore={loadingMore}
            onLoadMore={onLoadMore}
          />
        )}
      </Stack>
    );
  }

  // ── Tablet / phone: cards ──

  if (showSkeleton) {
    return <LoadingSkeleton actions={countCardActions(onResend, onEdit, onDelete)} />;
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-md)",
        }}
      >
        <AnimatePresence mode="sync">
          {invitations.map((invitation) => (
            <motion.div
              key={invitation.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <InvitationCard
                invitation={invitation}
                onClick={onEdit ? handleClick : undefined}
                onEdit={onEdit ? handleEdit : undefined}
                onDelete={onDelete ? handleDelete : undefined}
                onResend={onResend ? handleResend : undefined}
                highlighted={invitation.id === highlightedId}
                isDeleting={removingId === invitation.id}
                isResending={resendingId === invitation.id}
                forceClose={shouldForceClose(invitation.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(invitation.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(invitation.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <LoadMoreButton
        hasMore={hasMore}
        loading={loading}
        loadingMore={loadingMore}
        onLoadMore={onLoadMore}
      />
    </Stack>
  );
};

export const UserInvitationsList = React.memo(UserInvitationsListComponent);